import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { Provider } from '@supabase/supabase-js';

export type AuthUITextValue = ReactNode;

export interface AuthUIRoutes {
  login: string;
  register: string;
  forgotPassword: string;
  updatePassword: string;
  appHome: string;
}

export interface AuthUIBranding {
  logo?: ReactNode;
  brandingSubtitle?: AuthUITextValue;
  showBrandPanel: boolean;
}

export interface AuthUILayout {
  variant?: 'split' | 'stacked' | 'embedded';
  rootClassName?: string;
  brandPanelClassName?: string;
  formPanelClassName?: string;
  cardClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
}

export interface AuthUIRedirects {
  oauthRedirectTo?: string;
  signUpRedirectTo?: string;
  magicLinkRedirectTo?: string;
  passwordResetRedirectTo?: string;
}

export interface AuthUIProviderConfig {
  id: Provider;
  label?: AuthUITextValue;
  icon?: ReactNode;
  scopes?: string;
  redirectTo?: string;
  buttonVariant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
}

export interface AuthUIErrorTextOverrides {
  unknown?: AuthUITextValue;
  invalidCredentials?: AuthUITextValue;
  userAlreadyRegistered?: AuthUITextValue;
  emailNotConfirmed?: AuthUITextValue;
  invalidEmail?: AuthUITextValue;
  weakPassword?: AuthUITextValue;
  pleaseSignIn?: AuthUITextValue;
  passwordResetFailed?: AuthUITextValue;
  magicLinkFailed?: AuthUITextValue;
  oauthFailed?: AuthUITextValue;
  rateLimited?: AuthUITextValue;
  network?: AuthUITextValue;
  sessionExpired?: AuthUITextValue;
}

export interface AuthUITextOverrides {
  brandingSubtitle?: AuthUITextValue;
  signInTitle?: AuthUITextValue;
  signInSubtitle?: AuthUITextValue;
  signInMagicLinkSubtitle?: AuthUITextValue;
  signUpTitle?: AuthUITextValue;
  signUpSubtitle?: AuthUITextValue;
  signUpSuccessTitle?: AuthUITextValue;
  signUpSuccessSubtitle?: AuthUITextValue;
  forgotPasswordTitle?: AuthUITextValue;
  forgotPasswordSubtitle?: AuthUITextValue;
  resetLinkSentTitle?: AuthUITextValue;
  resetLinkSentSubtitle?: AuthUITextValue;
  magicLinkSentTitle?: AuthUITextValue;
  magicLinkSentSubtitle?: AuthUITextValue;
  updatePasswordTitle?: AuthUITextValue;
  updatePasswordSubtitle?: AuthUITextValue;
  updatePasswordExpiredSubtitle?: AuthUITextValue;
  passwordUpdatedTitle?: AuthUITextValue;
  passwordUpdatedSubtitle?: AuthUITextValue;
  checkYourEmail?: AuthUITextValue;
  checkYourEmailForLogin?: AuthUITextValue;
  checkYourEmailForReset?: AuthUITextValue;
  noAccount?: AuthUITextValue;
  alreadyAccount?: AuthUITextValue;
  useMagicLink?: AuthUITextValue;
  usePassword?: AuthUITextValue;
  orDivider?: AuthUITextValue;
  continue?: AuthUITextValue;
  emailLabel?: AuthUITextValue;
  emailPlaceholder?: string;
  passwordLabel?: AuthUITextValue;
  passwordPlaceholder?: string;
  confirmPasswordLabel?: AuthUITextValue;
  confirmPasswordPlaceholder?: string;
  newPasswordLabel?: AuthUITextValue;
  newPasswordPlaceholder?: string;
  signInButton?: AuthUITextValue;
  signUpButton?: AuthUITextValue;
  sendMagicLinkButton?: AuthUITextValue;
  resendMagicLinkButton?: AuthUITextValue;
  sendResetLinkButton?: AuthUITextValue;
  updatePasswordButton?: AuthUITextValue;
  forgotPasswordLink?: AuthUITextValue;
  backToLoginLink?: AuthUITextValue;
  signUpLink?: AuthUITextValue;
  signInLink?: AuthUITextValue;
  continueWithGoogle?: AuthUITextValue;
  errors?: AuthUIErrorTextOverrides;
}

export interface AuthUIConfig {
  text?: AuthUITextOverrides;
  routes?: Partial<AuthUIRoutes>;
  branding?: Partial<AuthUIBranding>;
  layout?: AuthUILayout;
  providers?: AuthUIProviderConfig[];
  redirects?: AuthUIRedirects;
}

const defaultRoutes: AuthUIRoutes = {
  login: '/auth/login',
  register: '/auth/register',
  forgotPassword: '/auth/forgot-password',
  updatePassword: '/auth/update-password',
  appHome: '/app',
};

const defaultBranding: AuthUIBranding = {
  logo: undefined,
  brandingSubtitle: 'Hire once, work forever.',
  showBrandPanel: true,
};

const defaultLayout: AuthUILayout = {
  variant: 'split',
  rootClassName: undefined,
  brandPanelClassName: undefined,
  formPanelClassName: undefined,
  cardClassName: undefined,
  titleClassName: undefined,
  subtitleClassName: undefined,
};

const defaultRedirects: AuthUIRedirects = {
  oauthRedirectTo: undefined,
  signUpRedirectTo: undefined,
  magicLinkRedirectTo: undefined,
  passwordResetRedirectTo: undefined,
};

export const AuthUIContext = createContext<AuthUIConfig | undefined>(undefined);

export const useAuthUI = () => {
  const ui = useContext(AuthUIContext);

  return {
    config: ui,
    text: ui?.text ?? {},
    routes: { ...defaultRoutes, ...ui?.routes },
    branding: { ...defaultBranding, ...ui?.branding },
    layout: { ...defaultLayout, ...ui?.layout },
    providers: ui?.providers ?? [],
    redirects: { ...defaultRedirects, ...ui?.redirects },
  };
};
